import { useState, useEffect, Fragment } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  Typography,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../store/appHooks";
import { CustomDialog } from "../../booking-documents/mui.styles";
import OTPInputs from "./OTPInputs";
import { handleSnackBar } from "../../../slices/global";
import MESSAGES from "../../../constants/messages";
import {
  resendOtp,
  setOtpVerification,
  setVerifyingOtp,
  verifyOtp,
} from "../../../slices/authReducers";
import { toMMSS } from "../../../utils/timestamp.util";
import { OTP_TYPE } from "../../../constants/general";
import EditIcon from "@mui/icons-material/Edit";
import Close from "@mui/icons-material/Close";

const OTPVerification = ({
  onSuccess,
  email,
  otpType,
  phone,
}: {
  onSuccess: any;
  email?: string;
  otpType: number;
  phone?: string;
}) => {
  const dispatch = useAppDispatch();
  const { authReducer }: any = useAppSelector((state) => state);
  const { otpTokenData, verifyingOtp }: any = authReducer;
  const [otp, setOtp] = useState(""); // TODO remove once email gets triggerd from API

  const RESEND_TIME_SEC = 20;
  const [allowResend, setAllowResend] = useState(false);
  const [timeSec, setTimeSec] = useState(RESEND_TIME_SEC);
  const [otpError, setOtpError] = useState("");

  useEffect(() => {
    return () => {
      dispatch(setVerifyingOtp(false));
      setOtp("");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChangeOtp = (v: string) => setOtp(v);

  const verifyOtpCall = () => {
    console.log(otpTokenData, "otpTokenData");
    if (otpTokenData?.token) {
      dispatch(
        verifyOtp({
          data: { token: otpTokenData?.token, otp },
          navigate: () => {
            onSuccess();
          },
        })
      );
    } else {
      dispatch(
        handleSnackBar({
          open: true,
          message: MESSAGES.APP_ERROR,
          severity: "error",
        })
      );
    }
  };

  const resend = () => {
    setTimeSec(0);
    dispatch(
      resendOtp({
        data: {
          ...(otpType === OTP_TYPE.EMAIL
            ? { email }
            : { mobile_number: phone }),
          otp_type: OTP_TYPE.EMAIL,
        },
        navigate: () => {
          setTimeSec(RESEND_TIME_SEC);
          setAllowResend(false);
        },
      })
    );
  };

  return authReducer.otpVerification ? (
    <Fragment>
      {/* <Box position="absolute" right={"2rem"}>
        <IconButton
          onClick={() => {
            dispatch(setOtpVerification(false));
          }}
        >
          <Close htmlColor="#000" style={{ fontSize: "2rem" }} />
        </IconButton>
      </Box> */}
      <Box
        sx={{
          padding: {
            xs: "24px",
            sm: "24px",
            md: "36px",
            lg: "36px",
            xl: "36px",
          },
        }}
      >
        <Typography
          color="#323232"
          fontSize={28}
          fontWeight={400}
          textAlign="left"
          lineHeight="34px"
        >
          OTP Verification for {otpType === OTP_TYPE.EMAIL ? `Email` : "Phone"}
        </Typography>

        <Typography
          color="#323232"
          fontSize={15}
          fontWeight={400}
          textAlign="left"
          mt={1.5}
          lineHeight={"22px"}
        >
          Enter 6-digit OTP sent on your registered{" "}
          {otpType === OTP_TYPE.EMAIL ? ` email` : " phone"}{" "}
          <span style={{ fontWeight: 500 }}>
            {" "}
            {otpType === OTP_TYPE.EMAIL ? email : phone}
          </span>
        </Typography>
        {/* <Box
          mt={2}
          display="flex"
          alignItems="center"
          justifyContent="center"
          gap={1}
        >
          <Typography fontSize={20} fontWeight={600}>
           
          </Typography>
          <Tooltip
            title={`Edit ${
              otpType === OTP_TYPE.EMAIL ? "email address" : "phone number"
            }`}
          >
            <IconButton
              size="small"
              style={{ border: "1px solid" }}
              onClick={() => {
                dispatch(setOtpVerification(false));
              }}
            >
              <EditIcon color="primary" />
            </IconButton >
          </Tooltip>
        </Box> */}
        <Box mt={1.5}>
          <OTPInputs
            wrapperProps={{ inputType: "number" }}
            onChange={onChangeOtp}
            validateForm={(e: any) => {
              setOtpError("");
              if (e.key === "Enter") {
                if (otp.length === 6) {
                  verifyOtpCall();
                } else {
                  setOtpError("Please enter 6 digit OTP");
                }
              }
            }}
          />
          {otpError && (
            <Typography color="error" fontSize={12} mt={1}>
              {otpError}
            </Typography>
          )}
        </Box>

        {timeSec > 0 && (
          <Box mt={1.5}>
            <Typography
              fontSize="15px"
              fontWeight={400}
              lineHeight="20px"
              color="#323232"
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
            >
              You will get OTP in:
              <Counter
                onTimerComplete={() => {
                  setAllowResend(true);
                  setTimeSec(0);
                }}
                timeSec={timeSec}
              />{" "}
            </Typography>
          </Box>
        )}

        <Box mt={3}>
          <Typography
            color="#A2A2A2"
            fontSize="15px"
            fontWeight={400}
            textAlign="center"
            lineHeight="18.15px"
          >
            Didn’t get OTP?{" "}
            <Typography
              fontSize="15px"
              component="span"
              color="#D21A00"
              fontWeight={400}
              style={{ cursor: "pointer", opacity: allowResend ? 1 : 0.5 }}
              onClick={() => allowResend && resend()}
            >
              Resend
            </Typography>
          </Typography>
        </Box>
        <Box mt={2}>
          <Button
            fullWidth
            variant="contained"
            disabled={otp.length !== 6 || verifyingOtp}
            size="large"
            endIcon={verifyingOtp ? <CircularProgress size={20} /> : null}
            onClick={verifyOtpCall}
          >
            {!verifyingOtp && `Verify Account`}
          </Button>
        </Box>
        <Box mt={2}>
          <Typography
            color="#323232"
            fontSize="15px"
            fontWeight={500}
            textAlign="center"
            lineHeight="18.15px"
          >
            Change your email?{" "}
            <span
              onClick={() => {
                dispatch(setOtpVerification(false));
              }}
              style={{ color: "#6E8C20" }}
            >
              Back to Sign Up
            </span>
          </Typography>
        </Box>
      </Box>
    </Fragment>
  ) : null;
  // <Box>
  //   <Dialog
  //     open={authReducer.otpVerification}
  //     PaperComponent={CustomDialog}
  //     fullWidth
  //     maxWidth="sm"
  //   >
  //     <DialogContent>
  //       <Box position="absolute" right={'2rem'}>
  //         <IconButton
  //           onClick={() => {
  //             dispatch(setOtpVerification(false))
  //           }}
  //         >
  //           <Close htmlColor="#000" style={{ fontSize: '2rem' }} />
  //         </IconButton>
  //       </Box>
  //       <Box sx={{ padding: { sm: 2, md: 3, lg: 4, xs: 1 } }}>
  //         <Typography
  //           color="#565656"
  //           fontSize={25}
  //           fontWeight={500}
  //           textAlign="center"
  //         >
  //           OTP Verification for{' '}
  //           {otpType === OTP_TYPE.EMAIL ? `Email` : 'Phone'}
  //         </Typography>

  //         <Typography
  //           color="#565656"
  //           fontSize={15}
  //           fontWeight={500}
  //           textAlign="center"
  //           mt={3}
  //         >
  //           Your Verification OTP has been sent to your{' '}
  //           {otpType === OTP_TYPE.EMAIL ? `Email` : 'Phone'}
  //         </Typography>
  //         <Box
  //           mt={2}
  //           display="flex"
  //           alignItems="center"
  //           justifyContent="center"
  //           gap={1}
  //         >
  //           <Typography fontSize={20} fontWeight={600}>
  //             {otpType === OTP_TYPE.EMAIL ? email : phone}
  //           </Typography>
  //           <Tooltip
  //             title={`Edit ${
  //               otpType === OTP_TYPE.EMAIL ? 'email address' : 'phone number'
  //             }`}
  //           >
  //             <IconButton
  //               size="small"
  //               style={{ border: '1px solid' }}
  //               onClick={() => {
  //                 dispatch(setOtpVerification(false))
  //               }}
  //             >
  //               <EditIcon color="primary" />
  //             </IconButton>
  //           </Tooltip>
  //         </Box>
  //         <Box mt={2}>
  //           <OTPInputs
  //             onChange={onChangeOtp}
  //             validateForm={(e: any) => {
  //               setOtpError('')
  //               if (e.key === 'Enter') {
  //                 if (otp.length === 6) {
  //                   verifyOtpCall()
  //                 } else {
  //                   setOtpError('Please enter 6 digit OTP')
  //                 }
  //               }
  //             }}
  //           />
  //           {otpError && (
  //             <Typography color="error" fontSize={12} mt={1}>
  //               {otpError}
  //             </Typography>
  //           )}
  //         </Box>

  //         <Box mt={3}>
  //           <Typography
  //             color="#565656"
  //             fontSize={13}
  //             fontWeight={500}
  //             textAlign="center"
  //           >
  //             Didn’t receive OTP?{' '}
  //             <Typography
  //               fontSize={13}
  //               component="span"
  //               color="primary"
  //               fontWeight={700}
  //               style={{ cursor: 'pointer', opacity: allowResend ? 1 : 0.5 }}
  //               onClick={() => allowResend && resend()}
  //             >
  //               Resend
  //             </Typography>
  //             {timeSec > 0 && (
  //               <Counter
  //                 onTimerComplete={() => {
  //                   setAllowResend(true)
  //                   setTimeSec(0)
  //                 }}
  //                 timeSec={timeSec}
  //               />
  //             )}
  //           </Typography>
  //         </Box>
  //         <Box mt={5}>
  //           <Button
  //             fullWidth
  //             variant="contained"
  //             disabled={otp.length !== 6 || verifyingOtp}
  //             size="large"
  //             endIcon={verifyingOtp ? <CircularProgress size={20} /> : null}
  //             onClick={verifyOtpCall}
  //           >
  //             {!verifyingOtp && `Verify Account`}
  //           </Button>
  //         </Box>
  //       </Box>
  //     </DialogContent>
  //   </Dialog>

  // </Box>
};

const Counter = ({
  onTimerComplete,
  timeSec,
}: {
  onTimerComplete: any;
  timeSec: number;
}) => {
  const [count, setCount] = useState(timeSec);

  useEffect(() => {
    //Implementing the setInterval method
    const interval = setInterval(() => {
      setCount(count - 1);
      if (count - 1 === 0) {
        onTimerComplete();
      }
    }, 1000);

    //Clearing the interval
    return () => clearInterval(interval);
  }, [count, onTimerComplete]);

  return <Typography>{toMMSS(count)}</Typography>;
};

export default OTPVerification;
